import { Result } from '../types/types'
import { WishlistItem } from '../../wishlist/components/WishlistItem'
import { formatMoney } from '../../helpers/formatMoney'
import { type_GM_STATE } from '../../helpers'
import useWindowSize from '../customHooks/useWindowSize'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Pagination } from 'swiper/modules'
import { intellisuggestTrackClick, redirect } from '../../helpers/searchspring-tracking'

declare var GM_STATE: type_GM_STATE
type Props = {
  product: Result
  isShowWishlist?: boolean
  isShowSecondImage?: boolean
  isShowPrice?: boolean
  isShowTitle?: boolean
}

const ProductCard = ({ product, isShowWishlist = true, isShowSecondImage = true, isShowPrice = true, isShowTitle = true }: Props): JSX.Element | null => {
  const productHasBadge = product.tags_badge || null
  const { defaultImage } = GM_STATE.shopify
  const { showProductBiLine } = GM_STATE.product
  const windowSize = useWindowSize()
  const isMobile = windowSize.width <= 640
  const stringToJson = (str: any) => JSON.parse(str?.replace(/&quot;/g, '"'))

  if (!product) return null
  const necklacePrice = product?.mfield_custom_necklace_price ? stringToJson(product?.mfield_custom_necklace_price)?.amount.replace('.00', '') : null
  const imageUrl = product.image ? product.image : defaultImage
  const hasFromLabel = product.ss_tags.includes('FROM') || product.ss_tags.includes('from') || product.ss_tags.includes('template_samepair')
  const minPrice = necklacePrice ? parseFloat(product.price) + parseFloat(necklacePrice) : product.price
  const rootUrlWithoutSlash = window.routes.rootUrlWithoutSlash
  const productUrl = product.url
  const productUrlWithLocale = !rootUrlWithoutSlash || (rootUrlWithoutSlash && productUrl.includes(rootUrlWithoutSlash))
    ? productUrl
    : rootUrlWithoutSlash + productUrl

  const decodeHTML = (text: string) => {
    const parser = new DOMParser()
    const decodedString = parser.parseFromString(text, 'text/html').documentElement.textContent

    return decodedString
  }

  const handleClickTracking = async (e: any) => {
    e.preventDefault()

    if (product.intellisuggestData && product.intellisuggestSignature) {
      intellisuggestTrackClick(productUrlWithLocale, product.intellisuggestData, product.intellisuggestSignature)

      redirect(productUrlWithLocale, e.ctrlKey)
    } else {
      redirect(productUrlWithLocale, e.ctrlKey)
    }
  }

  const wishlistWIgnoreTemplate = GM_STATE.wishlist.productTemplates.ignore

  const ComponentHandler = (): JSX.Element => {
    if (isMobile) {
      return (
        <>
          <div className="relative group">
            <Swiper
              navigation={false}
              scrollbar={false}
              pagination={{ clickable: true }}
              modules={[Pagination]}
              spaceBetween={0}
              slidesPerView={1}
              className="swiper-list-wishlist"
            >
              <SwiperSlide className="w-full overflow-hidden aspect-[170/212] lg:aspect-[335/419]" key={`${product.id}-image-1`}>
                <a href={productUrlWithLocale} onClick={handleClickTracking} title={product.name}>
                  <img src={`${imageUrl}&width=400`} className="object-cover w-full h-full" alt={product.name} loading="lazy" />
                </a>
              </SwiperSlide>
              {isShowSecondImage && product.ss_image_hover && (
                <SwiperSlide className="w-full overflow-hidden aspect-[170/212] lg:aspect-[335/419]" key={`${product.id}-image-2`}>
                  <a href={productUrlWithLocale} onClick={handleClickTracking} title={product.name}>
                    <img src={`${product.ss_image_hover}&width=400`} className="object-cover w-full h-full" alt={`${product.name} hover`} loading="lazy" />
                  </a>
                </SwiperSlide>
              )}
              {productHasBadge && !!productHasBadge?.length &&
                <div className="SS-label-container">
                  <div className="SS-label">
                    {product.tags_badge}
                  </div>
                </div>
              }
            </Swiper>
            {!wishlistWIgnoreTemplate.includes(product.template_suffix) && isShowWishlist && GM_STATE.wishlist.enable && (
              <div className="absolute top-2.5 right-2.5 transition-all duration-[350ms] ease-in-out z-10 lg:block hidden">
                <WishlistItem productId={Number(product.uid)} productHandle={product.handle} productTemplate="default" />
              </div>
            )}

            <div className="flex flex-col mt-XS mb-XS">
              {isShowTitle && <a href={productUrlWithLocale} onClick={handleClickTracking} title={product.name}>
                <h3 className="uppercase font-bstSpyre tracking-[1%] text-[12px] md:text-[14px] leading-[150%] md:leading-[120%] text-base-2">{product.name}</h3>
              </a>}
              {isShowPrice && <div className="flex justify-between mt-XS">
                <div className="flex justify-start gap-1">
                  {Number(product.msrp) > Number(product.price) ? (
                    <>
                      <p className="text-xs uppercase font-calibre font-light">
                        {hasFromLabel ? 'FROM' : ''} {formatMoney(Number(product.price) * 100)}
                      </p>
                      <p className="text-xs uppercase font-calibre line-through opacity-40 font-light">
                        {formatMoney(Number(product.msrp) * 100)}
                      </p>
                    </>
                  ) : (
                    <p className="text-xs uppercase font-calibre font-light">
                      {hasFromLabel ? 'FROM' : ''} {formatMoney(Number(minPrice) * 100)}
                    </p>
                  )}
                </div>
              </div>}
            </div>
          </div>
        </>
      )
    }

    return (
      <div className="flex flex-col h-full group">
        <div className="relative w-full h-auto overflow-hidden aspect-[170/212] lg:aspect-[335/419]">
          <a href={productUrlWithLocale} onClick={handleClickTracking} title={product.name}>
            <img src={`${imageUrl}&width=1200`} alt={product.name} className="w-full h-auto object-cover" loading="lazy" />
            {product.ss_image_hover && (
              <img
                src={`${product.ss_image_hover}&width=1200`}
                alt={`${product.name} hover`}
                className="absolute top-0 left-0 w-full h-full object-cover transition-opacity duration-300 opacity-0 group-hover:opacity-100"
                loading="lazy"
              />
            )}
          </a>
          {!wishlistWIgnoreTemplate.includes(product.template_suffix) && isShowWishlist && GM_STATE.wishlist.enable && (
            <div className="wishlist-icon absolute top-2.5 right-2.5 transition-opacity duration-300 lg:opacity-0 lg:group-hover:opacity-100">
              <WishlistItem productId={Number(product.uid)} productHandle={product.handle} productTemplate="default" />
            </div>
          )}
          {productHasBadge && !!productHasBadge?.length &&
            <div className="SS-label-container">
              <div className="SS-label">
                {product.tags_badge}
              </div>
            </div>
          }
        </div>
        <div className="relative flex flex-col mt-XS mb-XS">
          <div
            className={`flex flex-col transition-opacity duration-300 ${
              showProductBiLine && product?.mfield_custom_product_bi_line !== undefined ? 'lg:group-hover:opacity-0' : ''
            }`}
          >
            {isShowTitle && <a href={productUrlWithLocale} onClick={handleClickTracking}>
              <h3 className="uppercase font-bstSpyre tracking-[1%] text-[12px] md:text-[14px] leading-[150%] md:leading-[120%] text-base-2">{decodeHTML(product.name)}</h3>
            </a>}
            {isShowPrice && <div className="flex justify-between mt-XS">
              <div className="flex justify-start gap-1">
                {Number(product.msrp) > Number(product.price) ? (
                  <>
                    <p className="text-xs uppercase font-calibre font-light">
                      {hasFromLabel ? 'FROM' : ''} {formatMoney(Number(product.price) * 100)}
                    </p>
                    <p className="text-xs uppercase font-calibre line-through opacity-40 font-light">
                      {formatMoney(Number(product.msrp) * 100)}
                    </p>
                  </>
                ) : (
                  <p className="text-xs uppercase font-calibre font-light">
                    {hasFromLabel ? 'FROM' : ''} {formatMoney(Number(minPrice) * 100)}
                  </p>
                )}
              </div>
            </div>}
          </div>
          {showProductBiLine && product?.mfield_custom_product_bi_line !== undefined ? (
            <div className="absolute top-0 left-0 w-full transition-opacity duration-300 opacity-0 group-hover:opacity-100 lg-max:hidden">
              {isShowTitle && <div className="flex align-baseline justify-between gap-2.5 w-full">
                <a href={productUrlWithLocale} onClick={handleClickTracking}>
                  <h3 className="font-bstSpyre text-base-2">{decodeHTML(product.mfield_custom_product_bi_line)}</h3>
                </a>
              </div>}
              {isShowPrice && <div className="flex justify-start gap-1 mt-XS">
                {Number(product.msrp) > Number(product.price) ? (
                  <>
                    <p className="text-xs uppercase font-calibre font-light">
                      {hasFromLabel ? 'FROM' : ''} {formatMoney(Number(product.price) * 100)}
                    </p>
                    <p className="text-xs uppercase font-calibre line-through opacity-40 font-light">
                      {formatMoney(Number(product.msrp) * 100)}
                    </p>
                  </>
                ) : (
                  <p className="text-xs uppercase font-calibre font-light">
                    {hasFromLabel ? 'FROM' : ''} {formatMoney(Number(minPrice) * 100)}
                  </p>
                )}
              </div>}
            </div>
          ) : null}
        </div>
      </div>
    )
  }

  return <ComponentHandler />
}

export default ProductCard
